import React, { useEffect, useRef, useState } from "react";

// Client ID is public (it ships in the bundle). Set it in frontend/.env.local
// as REACT_APP_GOOGLE_CLIENT_ID. Treated as "configured" only when present
// and not the README placeholder, so the page still works before setup.
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const isConfigured = !!CLIENT_ID && !CLIENT_ID.startsWith("REPLACE_WITH");
const GSI_SRC = "https://accounts.google.com/gsi/client";

// Load the Google Identity Services script exactly once, even across
// multiple button mounts (Login + SignUp).
let gisPromise = null;
function loadGis() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.google && window.google.accounts && window.google.accounts.id) {
    return Promise.resolve();
  }
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GSI_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("GIS load failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = GSI_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("GIS load failed"));
    document.head.appendChild(s);
  });
  return gisPromise;
}

// Renders the official "Sign in with Google" button and hands the Google
// ID token (a JWT) back to the parent via onCredential. The parent posts
// it to /api/auth/google. When the client ID isn't configured yet, renders
// a disabled placeholder so the surrounding form still works.
const GoogleSignInButton = ({ onCredential, onError, text = "continue_with" }) => {
  const divRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isConfigured) return undefined;
    let cancelled = false;

    // GIS renderButton takes a fixed pixel width (it can't be 100%). Size
    // it to the container, clamped to GIS's [200, 400] range, so it fills
    // the panel like the other buttons instead of overflowing on mobile.
    const renderBtn = () => {
      if (cancelled || !divRef.current || !window.google) return;
      const avail = divRef.current.offsetWidth || 320;
      const width = Math.max(200, Math.min(400, Math.floor(avail)));
      window.google.accounts.id.renderButton(divRef.current, {
        theme: "outline",
        size: "large",
        width,
        text,
        shape: "rectangular",
        logo_alignment: "center",
      });
    };

    loadGis()
      .then(() => {
        if (cancelled || !divRef.current || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: (resp) => {
            if (resp && resp.credential) {
              onCredential(resp.credential);
            } else if (onError) {
              onError(new Error("No credential returned from Google"));
            }
          },
        });
        renderBtn();
        window.addEventListener("resize", renderBtn);
      })
      .catch((e) => {
        setFailed(true);
        if (onError) onError(e);
      });
    return () => {
      cancelled = true;
      window.removeEventListener("resize", renderBtn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isConfigured) {
    return (
      <div style={{ textAlign: "center", margin: "var(--sp-1) 0" }}>
        <button
          type="button"
          className="sci-btn sci-btn--ghost sci-btn--full"
          disabled
        >
          Continue with Google
        </button>
        <div
          style={{
            fontSize: "var(--fs-xs)",
            color: "var(--text-muted)",
            marginTop: "var(--sp-2)",
          }}
        >
          Google sign-in isn't configured yet (set REACT_APP_GOOGLE_CLIENT_ID).
        </div>
      </div>
    );
  }

  return (
    <div style={{ margin: "var(--sp-1) 0" }}>
      <div ref={divRef} style={{ display: "flex", justifyContent: "center" }} />
      {failed && (
        <div className="sci-alert sci-alert--danger" style={{ marginTop: "var(--sp-2)" }}>
          <div className="sci-alert__icon" />
          <span>Couldn't load Google sign-in. Check your connection and try again.</span>
        </div>
      )}
    </div>
  );
};

export default GoogleSignInButton;
